/**
 * Users Store
 *
 * Zustand store for managing user list state including:
 * - Paginated list of users
 * - Loading and error states
 * - Actions for loading users, inviting new users, and resending invitations
 *
 * Uses the UsersApiClient from the registry to communicate with the ZITADEL API.
 *
 * @module stores/useUsersStore
 */

import { create, type StateCreator } from 'zustand';
import type { UsersApiClient } from '../api/UsersApiClient';
import type { User, PaginationMeta, UserRole } from '../types';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../types';
import { usersClient as defaultUsersClient } from '../api/registry';
import { useAuthStore } from './useAuthStore';
import { formatErrorMessage } from '../utils';
import { isHttpError } from '../api/http';
import {
  isSeatLimitError,
  isDuplicateEmailError,
  isOrgNotEntitledError,
} from '../types/errors';

// =============================================================================
// Types
// =============================================================================

/**
 * Error types that can be thrown by inviteUser action.
 * These allow the UI to display appropriate error messages.
 */
export type InviteUserErrorType =
  | 'SEAT_LIMIT'
  | 'DUPLICATE_EMAIL'
  | 'ORG_NOT_ENTITLED'
  | 'GENERIC';

/**
 * Extended error with type information for invite operations.
 */
export interface InviteUserError extends Error {
  errorType: InviteUserErrorType;
}

/**
 * Creates a typed invite user error.
 */
function createInviteUserError(
  message: string,
  errorType: InviteUserErrorType
): InviteUserError {
  const error = new Error(message) as InviteUserError;
  error.errorType = errorType;
  return error;
}

/**
 * Type guard to check if an error is an InviteUserError.
 */
export function isInviteUserError(error: unknown): error is InviteUserError {
  return (
    error instanceof Error &&
    'errorType' in error &&
    typeof (error as InviteUserError).errorType === 'string'
  );
}

/**
 * Error categories surfaced by the {@link UsersState.deleteUser} action so the
 * confirm dialog can render an appropriate, user-friendly message. Mirrors the
 * backend's APIError codes for the delete endpoint.
 *
 * - `NOT_FOUND`        — the user isn't in this org (404 USER_NOT_FOUND).
 * - `FORBIDDEN`        — the caller lacks authority / ZITADEL denied the op
 *                        (403 FORBIDDEN). Surfaced as "not permitted".
 * - `CANNOT_REMOVE_SELF` — admin tried to delete their own account
 *                        (422 CANNOT_REMOVE_SELF).
 * - `GENERIC`          — any other failure (incl. 502 upstream).
 */
export type DeleteUserErrorType =
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'CANNOT_REMOVE_SELF'
  | 'GENERIC';

/**
 * Extended error with type information for delete operations.
 */
export interface DeleteUserError extends Error {
  errorType: DeleteUserErrorType;
}

/**
 * Creates a typed delete user error.
 */
function createDeleteUserError(
  message: string,
  errorType: DeleteUserErrorType,
): DeleteUserError {
  const error = new Error(message) as DeleteUserError;
  error.errorType = errorType;
  return error;
}

/**
 * Type guard to check if an error is a DeleteUserError.
 */
export function isDeleteUserError(error: unknown): error is DeleteUserError {
  return (
    error instanceof Error &&
    'errorType' in error &&
    typeof (error as DeleteUserError).errorType === 'string'
  );
}

/**
 * Error categories surfaced by the {@link UsersState.updateUserRole} action so
 * the confirm dialog can render an appropriate message. Mirrors the backend's
 * APIError codes for the role endpoint.
 *
 * - `NOT_FOUND`          — the user isn't in this org (404 USER_NOT_FOUND).
 * - `FORBIDDEN`          — the caller lacks authority (403 FORBIDDEN).
 * - `CANNOT_MODIFY_SELF` — the caller tried to change their own role
 *                          (422 CANNOT_MODIFY_SELF).
 * - `NO_ACCESS`          — the target's access was revoked; there is no role
 *                          grant to change (409 USER_HAS_NO_ACCESS).
 * - `GENERIC`            — any other failure (incl. 502 upstream).
 */
export type UpdateRoleErrorType =
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'CANNOT_MODIFY_SELF'
  | 'NO_ACCESS'
  | 'GENERIC';

/**
 * Extended error with type information for role-change operations.
 */
export interface UpdateRoleError extends Error {
  errorType: UpdateRoleErrorType;
}

/**
 * Creates a typed role-change error.
 */
function createUpdateRoleError(
  message: string,
  errorType: UpdateRoleErrorType,
): UpdateRoleError {
  const error = new Error(message) as UpdateRoleError;
  error.errorType = errorType;
  return error;
}

/**
 * Type guard to check if an error is an UpdateRoleError.
 */
export function isUpdateRoleError(error: unknown): error is UpdateRoleError {
  return (
    error instanceof Error &&
    'errorType' in error &&
    typeof (error as UpdateRoleError).errorType === 'string'
  );
}

/**
 * Users store state shape.
 */
export interface UsersState {
  // State
  users: User[];
  pagination: PaginationMeta;
  isLoading: boolean;
  error: Error | null;
  successMessage: string | null;
  /**
   * Non-fatal warning from the last member operation — a partial success the
   * caller must still see. Set from the invite response's `warning` field: the
   * invite succeeded (user created, product role granted) but a follow-on step
   * did not (e.g. the invited admin's org-management membership could not be
   * granted because the caller lacks ORG_OWNER). Distinct from `successMessage`
   * so the UI can style it as a warning rather than a confirmation, and never
   * silently drops it. `null` when the last operation had no caveat.
   */
  warningMessage: string | null;
  /**
   * Whether the caller may manage members in the currently-targeted org. Set
   * from the list response's `canManage` on every successful load — a proactive
   * signal combining the caller's live ZITADEL user-management authority and the
   * org's platform-project entitlement. Drives the invite affordance BEFORE any
   * attempt (button shown only when an invite would succeed), replacing the old
   * reactive "rejected with ORG_NOT_ENTITLED" flag.
   *
   * Tri-state: `null` = UNRESOLVED (no load has completed for the current org —
   * the initial state and after {@link UsersState.setTargetOrg} switches orgs);
   * `true`/`false` = the resolved verdict. Fail-closed for the *action* (the
   * invite button treats `null` and `false` alike as disabled), but the
   * disabled-explanation notice must distinguish them: it shows only on an
   * explicit `false`, never on `null`, so a capable caller doesn't see a
   * false-negative "you can't manage" flash while the first load is in flight.
   */
  canManageTargetOrg: boolean | null;

  /**
   * Target organization for member operations, selected by a **staff admin**
   * via the org picker. `null` means "no explicit target" — operations then
   * scope to the user's own active org (the only mode normal admins ever use).
   *
   * The effective org id (`targetOrgId ?? activeOrgId`) is materialized into the
   * mandatory `/orgs/{orgId}/users…` path segment on every list/invite/resend
   * call. When neither is available the store fails closed and skips the request
   * rather than build a malformed URL.
   */
  targetOrgId: string | null;

  // Actions
  loadUsers: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setPageSize: (pageSize: number) => Promise<void>;
  inviteUser: (
    email: string,
    firstName: string,
    lastName: string,
    role: UserRole,
  ) => Promise<void>;
  resendInvite: (userId: string) => Promise<void>;
  /**
   * Remove a member from the currently-targeted org, then reload the page.
   *
   * Scopes to the effective org id (`targetOrgId ?? activeOrgId`). On success
   * sets a confirmation `successMessage` and refetches the current page so the
   * grid reflects the removal. On failure throws a {@link DeleteUserError} with
   * a user-friendly message (the caller — the confirm dialog — surfaces it
   * inline and keeps itself open); store-level `error` is left untouched so a
   * failed delete doesn't replace the grid with a page-level error.
   */
  deleteUser: (userId: string) => Promise<void>;
  /**
   * Change a member's platform role in the currently-targeted org, then reload.
   *
   * Scopes to the effective org id (`targetOrgId ?? activeOrgId`). On success
   * sets a confirmation `successMessage` (plus `warningMessage` on a partial
   * success — the role was updated but the org-membership sync had a gap) and
   * refetches the current page so the grid reflects the new role. On failure
   * throws an {@link UpdateRoleError} for the confirm dialog to surface inline;
   * store-level `error` is left untouched.
   */
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
  /**
   * Select the target org (staff admins) and reload its members.
   *
   * Resets pagination to page 1, clears any prior error / entitlement flag /
   * success message (they belong to the previously-targeted org), then fetches
   * the new org's first page. Pass `null` to clear the target and fall back to
   * the active org.
   */
  setTargetOrg: (orgId: string | null) => Promise<void>;
  clearSuccessMessage: () => void;
  clearWarningMessage: () => void;
  clearError: () => void;
}

// =============================================================================
// Default Pagination
// =============================================================================

/**
 * Default pagination state.
 */
const DEFAULT_PAGINATION: PaginationMeta = {
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  totalCount: 0,
  totalPages: 0,
};

// =============================================================================
// Error Messages
// =============================================================================

/**
 * Message shown when the caller can't manage members in the targeted org — the
 * proactive disabled-invite state, driven by {@link UsersState.canManageTargetOrg}.
 * Deliberately covers BOTH reasons the capability can be false (the caller lacks
 * ZITADEL user-management authority in the org, OR the org isn't entitled to the
 * platform project), since the SPA gets a single boolean and doesn't distinguish
 * them. Exported so the page and dialog render identical, non-alarming copy.
 */
export const CANNOT_MANAGE_MEMBERS_MESSAGE =
  "You don't have permission to manage members in this organization.";

/**
 * Message for the reactive `ORG_NOT_ENTITLED` invite rejection (defensive: the
 * proactive gate normally prevents reaching it). Kept for the error mapping.
 */
export const ORG_NOT_ENTITLED_MESSAGE =
  "This organization can't manage platform team members.";

/**
 * Message shown when no organization can be resolved to scope the request to —
 * no staff-picked target AND no active org in the backend identity. This is a
 * fail-closed degenerate case (e.g. a malformed identity); we surface it rather
 * than issue a request to a malformed `/orgs//users` URL.
 */
export const NO_ORG_SELECTED_MESSAGE =
  'No organization is selected. Please pick an organization to continue.';

/**
 * User-friendly error messages for specific error scenarios.
 */
const ERROR_MESSAGES = {
  SEAT_LIMIT: 'Seat limit reached — contact ModernPath',
  DUPLICATE_EMAIL: 'User already exists in this organization',
  ORG_NOT_ENTITLED: ORG_NOT_ENTITLED_MESSAGE,
  GENERIC: 'Something went wrong. Please try again.',
} as const;

/**
 * User-friendly error messages for the delete-member flow, keyed by
 * {@link DeleteUserErrorType}. Exported so the confirm dialog and tests share
 * identical copy. `FORBIDDEN` is deliberately broad: it covers both a missing
 * authority grant and backend guard rails (e.g. last admin / self-removal),
 * which the SPA does not attempt to distinguish from the status alone.
 */
export const DELETE_USER_ERROR_MESSAGES: Record<DeleteUserErrorType, string> = {
  NOT_FOUND: 'This user no longer exists in the organization.',
  FORBIDDEN: "You're not permitted to delete this user.",
  CANNOT_REMOVE_SELF: "You can't delete your own account.",
  GENERIC: 'Something went wrong deleting the user. Please try again.',
} as const;

/**
 * User-friendly error messages for the role-change flow, keyed by
 * {@link UpdateRoleErrorType}. Exported so the confirm dialog and tests share
 * identical copy.
 */
export const UPDATE_ROLE_ERROR_MESSAGES: Record<UpdateRoleErrorType, string> = {
  NOT_FOUND: 'This user no longer exists in the organization.',
  FORBIDDEN: "You're not permitted to change this user's role.",
  CANNOT_MODIFY_SELF: "You can't change your own role.",
  NO_ACCESS:
    "This user's access has been revoked, so their role can't be changed.",
  GENERIC: 'Something went wrong changing the role. Please try again.',
} as const;

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Resolves the current user's active-org id from the auth store. Used as the
 * fallback org for normal admins (who have no staff-picked target). Injectable
 * via {@link createUsersStoreCreator} so this store's tests stay isolated from
 * the auth store.
 */
const defaultGetActiveOrgId = (): string | null =>
  useAuthStore.getState().activeOrgId;

/**
 * Creates the users store state creator with optional dependency overrides.
 *
 * @param usersClient - Optional users client override (defaults to registry client)
 * @param getActiveOrgId - Optional resolver for the active-org id fallback
 *   (defaults to reading `activeOrgId` from the auth store)
 * @returns Zustand state creator function
 */
export function createUsersStoreCreator(
  usersClient: UsersApiClient = defaultUsersClient,
  getActiveOrgId: () => string | null = defaultGetActiveOrgId,
): StateCreator<UsersState> {
  return (set, get) => {
    /**
     * Resolve the org id to scope member operations to: the staff-picked target
     * org if set, otherwise the user's own active org. `null` when neither is
     * available (fail closed — callers skip the request).
     */
    const resolveOrgId = (): string | null =>
      get().targetOrgId ?? getActiveOrgId();

    /**
     * Internal helper to load users with current pagination.
     * Implements stale-data-on-failure behavior.
     */
    const loadUsersInternal = async (): Promise<void> => {
      const orgId = resolveOrgId();
      if (!orgId) {
        // Fail closed: no org to scope to. Surface a clear error and keep any
        // existing users rather than calling a malformed `/orgs//users` URL.
        set((state) => ({
          isLoading: false,
          error: new Error(NO_ORG_SELECTED_MESSAGE),
          users: state.users,
        }));
        return;
      }

      set({ isLoading: true, error: null });

      try {
        const { page, pageSize } = get().pagination;
        const response = await usersClient.listUsers(orgId, { page, pageSize });

        set({
          users: response.items,
          pagination: response.pagination,
          isLoading: false,
          // Proactive invite gate for the org just loaded. Absent (older backend)
          // → false (fail closed); the button stays disabled rather than offering
          // an invite that would fail.
          canManageTargetOrg: response.canManage ?? false,
        });
      } catch (error) {
        // Stale-data-on-failure: preserve existing users
        const formattedMessage = formatErrorMessage(error);
        set((state) => ({
          error: error instanceof Error ? error : new Error(formattedMessage),
          isLoading: false,
          // Keep existing users on failure
          users: state.users,
        }));
      }
    };

    return {
      // Initial state
      users: [],
      pagination: { ...DEFAULT_PAGINATION },
      isLoading: false,
      error: null,
      successMessage: null,
      warningMessage: null,
      canManageTargetOrg: null,
      targetOrgId: null,

      /**
       * Load Users - Fetches a page of users.
       *
       * 1. Set isLoading=true, clear error
       * 2. Call listUsers on the users client with current pagination parameters
       * 3. On success, set users and pagination from response, clear error
       * 4. On failure, set error but preserve existing users (stale-data-on-failure)
       * 5. Set isLoading=false
       */
      loadUsers: loadUsersInternal,

      /**
       * Set Page - Changes the current page.
       *
       * 1. Update pagination.page
       * 2. Call loadUsers to fetch the new page
       */
      setPage: async (page: number) => {
        set((state) => ({
          pagination: {
            ...state.pagination,
            page,
          },
        }));
        await loadUsersInternal();
      },

      /**
       * Set Page Size - Changes the page size.
       *
       * 1. Update pagination.pageSize
       * 2. Reset pagination.page to 1
       * 3. Call loadUsers to fetch with new size
       */
      setPageSize: async (pageSize: number) => {
        set((state) => ({
          pagination: {
            ...state.pagination,
            pageSize,
            page: DEFAULT_PAGE, // Reset to first page when page size changes
          },
        }));
        await loadUsersInternal();
      },

      /**
       * Set Target Org - Selects the org a staff admin manages, then reloads.
       *
       * 1. Set targetOrgId
       * 2. Reset pagination to page 1 (the new org's user set is independent)
       * 3. Clear error and success message, and reset the per-org capability
       *    flag to null/UNRESOLVED — the prior org's verdict no longer applies,
       *    and null (not false) keeps the reload from flashing the "can't manage"
       *    notice before canManageTargetOrg resolves for the new org
       * 4. Load the new org's first page of users
       *
       * Pass `null` to clear the target (fall back to the active org).
       */
      setTargetOrg: async (orgId: string | null) => {
        set((state) => ({
          targetOrgId: orgId,
          pagination: {
            ...state.pagination,
            page: DEFAULT_PAGE,
          },
          error: null,
          successMessage: null,
          warningMessage: null,
          canManageTargetOrg: null,
        }));
        await loadUsersInternal();
      },

      /**
       * Invite User - Invites a new user.
       *
       * 1. Call inviteUser on the users client with email, name, and role
       * 2. On success, set successMessage with confirmation, call loadUsers to refresh
       * 3. On seat limit error, throw error with specific message
       * 4. On duplicate email error, throw error with specific message
       * 5. On other failure, throw generic error
       */
      inviteUser: async (
        email: string,
        firstName: string,
        lastName: string,
        role: UserRole,
      ) => {
        const orgId = resolveOrgId();
        if (!orgId) {
          // Fail closed: no org to scope the invite to.
          throw createInviteUserError(NO_ORG_SELECTED_MESSAGE, 'GENERIC');
        }

        try {
          const response = await usersClient.inviteUser(
            orgId,
            email,
            firstName,
            lastName,
            role,
          );

          // Set success message, and surface any partial-success warning the
          // backend returned (e.g. the admin org-membership gap) instead of
          // silently dropping it. `null` overwrites any stale warning from a
          // prior invite.
          set({
            successMessage: `Invitation sent to ${email}`,
            warningMessage: response.warning ?? null,
          });

          // Refresh the user list
          await loadUsersInternal();
        } catch (error) {
          // Check for specific error types
          if (isHttpError(error)) {
            const apiError = error.toApiError();

            if (isSeatLimitError(apiError)) {
              throw createInviteUserError(ERROR_MESSAGES.SEAT_LIMIT, 'SEAT_LIMIT');
            }

            if (isDuplicateEmailError(apiError)) {
              throw createInviteUserError(ERROR_MESSAGES.DUPLICATE_EMAIL, 'DUPLICATE_EMAIL');
            }

            // Defensive: the proactive canManageTargetOrg gate normally prevents
            // an invite into a non-entitled org, but if one slips through, surface
            // the same clear, non-alarming message rather than a scary failure.
            // Also pin canManageTargetOrg=false so the affordance disables.
            if (isOrgNotEntitledError(apiError)) {
              set({ canManageTargetOrg: false });
              throw createInviteUserError(
                ERROR_MESSAGES.ORG_NOT_ENTITLED,
                'ORG_NOT_ENTITLED'
              );
            }

            // Check by status code and message as fallback
            if (apiError.status === 403 && apiError.message?.toLowerCase().includes('seat')) {
              throw createInviteUserError(ERROR_MESSAGES.SEAT_LIMIT, 'SEAT_LIMIT');
            }

            if (apiError.status === 409 && apiError.message?.toLowerCase().includes('exist')) {
              throw createInviteUserError(ERROR_MESSAGES.DUPLICATE_EMAIL, 'DUPLICATE_EMAIL');
            }
          }

          // Generic error
          throw createInviteUserError(ERROR_MESSAGES.GENERIC, 'GENERIC');
        }
      },

      /**
       * Resend Invite - Resends invitation for a user.
       *
       * 1. Call resendInvite on the users client with user ID
       * 2. On success, set successMessage with confirmation
       * 3. On failure, set error
       */
      resendInvite: async (userId: string) => {
        const orgId = resolveOrgId();
        if (!orgId) {
          // Fail closed: no org to scope the resend to.
          set({ error: new Error(NO_ORG_SELECTED_MESSAGE) });
          return;
        }

        try {
          await usersClient.resendInvite(orgId, userId);

          // Set success message
          set({ successMessage: 'Invitation resent' });
        } catch (error) {
          // Set error state
          const formattedMessage = formatErrorMessage(error);
          set({
            error: error instanceof Error ? error : new Error(formattedMessage),
          });
        }
      },

      /**
       * Delete User - Permanently deletes a member's account, then reloads.
       *
       * The backend performs a real ZITADEL account deletion (the user is
       * resource-owned by this org). On success the row disappears after the
       * refetch.
       *
       * 1. Resolve the effective org id (fail closed if none).
       * 2. Call deleteUser on the users client.
       * 3. On success — or on a 404 (idempotent: the account is already gone) —
       *    set a confirmation successMessage and refetch the current page.
       * 4. On other failures, throw a typed {@link DeleteUserError} so the
       *    confirm dialog can show an inline message and stay open. The
       *    store-level `error` (page-level alert) is intentionally NOT set —
       *    a failed delete shouldn't blank the grid.
       */
      deleteUser: async (userId: string) => {
        const orgId = resolveOrgId();
        if (!orgId) {
          // Fail closed: no org to scope the delete to.
          throw createDeleteUserError(NO_ORG_SELECTED_MESSAGE, 'GENERIC');
        }

        try {
          await usersClient.deleteUser(orgId, userId);

          // Confirmation + refresh the current page so the row disappears.
          set({ successMessage: 'User deleted' });
          await loadUsersInternal();
        } catch (error) {
          if (isHttpError(error)) {
            const apiError = error.toApiError();

            // 404 is idempotent success: the account is already gone. Treat it
            // as a successful delete (refresh so the stale row disappears).
            if (apiError.status === 404) {
              set({ successMessage: 'User deleted' });
              await loadUsersInternal();
              return;
            }

            // 422 CANNOT_REMOVE_SELF — admin tried to delete themselves.
            if (apiError.status === 422) {
              throw createDeleteUserError(
                DELETE_USER_ERROR_MESSAGES.CANNOT_REMOVE_SELF,
                'CANNOT_REMOVE_SELF',
              );
            }

            // 403 FORBIDDEN — missing authority or ZITADEL denied the op.
            if (apiError.status === 403) {
              throw createDeleteUserError(
                DELETE_USER_ERROR_MESSAGES.FORBIDDEN,
                'FORBIDDEN',
              );
            }
          }

          throw createDeleteUserError(
            DELETE_USER_ERROR_MESSAGES.GENERIC,
            'GENERIC',
          );
        }
      },

      /**
       * Update User Role - Changes a member's platform role, then reloads.
       *
       * 1. Resolve the effective org id (fail closed if none).
       * 2. Call updateUserRole on the users client.
       * 3. On success, set a confirmation successMessage (and warningMessage on
       *    a partial success) and refetch the current page so the Role column
       *    reflects the change.
       * 4. On failure, throw a typed {@link UpdateRoleError} so the confirm
       *    dialog can show an inline message and stay open. Store-level `error`
       *    is intentionally NOT set — a failed role change shouldn't blank the
       *    grid.
       */
      updateUserRole: async (userId: string, role: UserRole) => {
        const orgId = resolveOrgId();
        if (!orgId) {
          // Fail closed: no org to scope the role change to.
          throw createUpdateRoleError(NO_ORG_SELECTED_MESSAGE, 'GENERIC');
        }

        try {
          const response = await usersClient.updateUserRole(orgId, userId, role);

          set({
            successMessage:
              role === 'admin' ? 'User is now an admin' : 'User is now a member',
            // Surface a partial-success caveat (org-membership sync gap)
            // instead of silently dropping it, mirroring the invite flow.
            warningMessage: response.warning ?? null,
          });
          await loadUsersInternal();
        } catch (error) {
          if (isHttpError(error)) {
            const apiError = error.toApiError();

            if (apiError.status === 404) {
              throw createUpdateRoleError(
                UPDATE_ROLE_ERROR_MESSAGES.NOT_FOUND,
                'NOT_FOUND',
              );
            }

            // 422 CANNOT_MODIFY_SELF — the caller targeted their own row (the
            // grid disables it; this is the defensive path).
            if (apiError.status === 422) {
              throw createUpdateRoleError(
                UPDATE_ROLE_ERROR_MESSAGES.CANNOT_MODIFY_SELF,
                'CANNOT_MODIFY_SELF',
              );
            }

            // 409 USER_HAS_NO_ACCESS — revoked user; nothing to change.
            if (apiError.status === 409) {
              throw createUpdateRoleError(
                UPDATE_ROLE_ERROR_MESSAGES.NO_ACCESS,
                'NO_ACCESS',
              );
            }

            if (apiError.status === 403) {
              throw createUpdateRoleError(
                UPDATE_ROLE_ERROR_MESSAGES.FORBIDDEN,
                'FORBIDDEN',
              );
            }
          }

          throw createUpdateRoleError(
            UPDATE_ROLE_ERROR_MESSAGES.GENERIC,
            'GENERIC',
          );
        }
      },

      /**
       * Clear Success Message - Clears the success message.
       *
       * Used after snackbar dismisses.
       */
      clearSuccessMessage: () => {
        set({ successMessage: null });
      },

      /**
       * Clear Warning Message - Clears the partial-success warning.
       *
       * Used after the warning snackbar dismisses.
       */
      clearWarningMessage: () => {
        set({ warningMessage: null });
      },

      /**
       * Clear Error - Clears the error.
       *
       * Used when retrying or dismissing error.
       */
      clearError: () => {
        set({ error: null });
      },
    };
  };
}

// =============================================================================
// Store Factory
// =============================================================================

/**
 * Creates a users store instance with optional dependency overrides.
 *
 * @param usersClient - Optional users client for testing
 * @param getActiveOrgId - Optional active-org id resolver for testing (defaults
 *   to reading `activeOrgId` from the auth store)
 * @returns Zustand store hook
 *
 * @example
 * ```typescript
 * // For testing with a mock client + fixed active org
 * const mockClient = new MockUsersApiClient();
 * const useTestStore = createUsersStore(mockClient, () => 'org-123');
 * ```
 */
export function createUsersStore(
  usersClient?: UsersApiClient,
  getActiveOrgId?: () => string | null,
) {
  return create<UsersState>(
    createUsersStoreCreator(usersClient, getActiveOrgId),
  );
}

// =============================================================================
// Default Export
// =============================================================================

/**
 * Default users store instance using the registry client.
 *
 * Use this for normal application usage:
 * ```typescript
 * import { useUsersStore } from './stores/useUsersStore';
 *
 * function UsersPage() {
 *   const { users, isLoading, loadUsers } = useUsersStore();
 *   // ...
 * }
 * ```
 */
export const useUsersStore = createUsersStore();
